'use client';

import { useState } from 'react';
import useVelocityAccountStore, {
	AccountData,
} from 'src/stores/useVelocityAccountsStore';
import useWalletIsConnected from './useWalletIsConnected';
import useUiUpdateInterval from './useUiUpdateInterval';

const DEFAULT_STATE: AccountData | undefined = undefined;

/**
 * Use this hook only if immediate account updates are necessary.
 *
 * NOTE: this subscribes directly to the account store, which is re-written on
 * every oracle tick (spot balances are re-synced against oracle prices), so the
 * returned reference changes many times per second. For expensive UI or live
 * client-derived values, prefer `useTickedAccountData` to throttle re-renders.
 */
const useAccountData = (): AccountData | undefined => {
	const isConnected = useWalletIsConnected();
	const currentAccount = useVelocityAccountStore((s) =>
		s.getCurrentUserAccount()
	);

	const accountData =
		isConnected && currentAccount ? currentAccount : DEFAULT_STATE;

	return accountData;
};

/**
 * Throttled variant of `useAccountData`. Returns the current account snapshot but
 * only propagates changes to consumers on the essential UI update interval
 * (`Env.uiEssentialUpdateIntervalMs`). Use this for expensive consumers so they
 * don't re-render on every account-store write.
 */
export const useTickedAccountData = (): AccountData | undefined => {
	const currentAccountData = useAccountData();

	const [accountData, setAccountData] = useState<AccountData | undefined>(
		currentAccountData
	);

	useUiUpdateInterval(() => setAccountData(currentAccountData), true, false);

	return accountData;
};

export default useAccountData;
